import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import PubBadge from "../../components/PubBadge";
import { Star, AlertTriangle } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function Tracking() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/tracking?date_from=${from}&date_to=${to}`);
      setData(r.data);
      setLoadError("");
    } catch {
      setLoadError("Could not load tracking. Please sign in again or refresh the page.");
    }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-sm text-[#5c5852]">{loadError || "Loading…"}</div>;
  const { counts, missing_step2, missing_step3, queue, kpis, targets } = data;

  return (
    <div className="p-6 lg:p-10" data-testid="tracking-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="btw-overline mb-1">Pipeline</p>
          <h1 className="font-serif text-3xl">Tracking</h1>
        </div>
        <div className="flex gap-3 items-center text-sm">
          <input type="date" data-testid="date-from" value={from} onChange={(e) => setFrom(e.target.value)} className="btw-input border border-[#dcd3c6] px-3 py-2 bg-white" />
          <span className="text-[#5c5852]">to</span>
          <input type="date" data-testid="date-to" value={to} onChange={(e) => setTo(e.target.value)} className="btw-input border border-[#dcd3c6] px-3 py-2 bg-white" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="kpi-panel">
        <Kpi label="Published on target (90d)" value={kpis.pct_on_target == null ? "—" : `${kpis.pct_on_target}%`} testid="kpi-on-target" />
        <Kpi label="Avg days → published (priority)" value={kpis.avg_days_priority ?? "—"} testid="kpi-avg-priority" />
        <Kpi label="Avg days → published (standard)" value={kpis.avg_days_standard ?? "—"} testid="kpi-avg-standard" />
        <Kpi label="Submissions / week" value={kpis.submissions_per_week} testid="kpi-subs-week" />
        <Kpi label="Published / week" value={kpis.published_per_week} testid="kpi-pub-week" />
        <Kpi label="Targets (priority / standard)" value={`${targets.priority_days}d / ${targets.standard_days}d`} testid="kpi-targets" />
      </div>

      {/* Counts */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="By status" testid="counts-status">
          <CountRows obj={counts.by_status} />
        </Panel>
        <Panel title="By publication" testid="counts-publication">
          <CountRows obj={counts.by_publication} upper />
        </Panel>
        <Panel title="By story type" testid="counts-type">
          <CountRows obj={counts.by_story_type} />
        </Panel>
      </div>

      {/* Incomplete */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Panel title="Started — missing vendor credits" testid="missing-step2">
          {missing_step2.length === 0 && <Empty>None — great.</Empty>}
          {missing_step2.map((e) => <IncompleteRow key={e.id} e={e} />)}
        </Panel>
        <Panel title="Credits done — missing story & submit" testid="missing-step3">
          {missing_step3.length === 0 && <Empty>None — great.</Empty>}
          {missing_step3.map((e) => <IncompleteRow key={e.id} e={e} />)}
        </Panel>
      </div>

      {/* Queue with lead times */}
      <Panel title="Publishing queue (priority first, then oldest submitted)" testid="queue-panel">
        {queue.length === 0 && <Empty>Nothing waiting to be published.</Empty>}
        {queue.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#5c5852] border-b border-[#dcd3c6]">
                  <th className="text-left py-2 pr-3 w-6"></th>
                  <th className="text-left py-2 pr-3">Submission</th>
                  <th className="text-left py-2 pr-3">Pub</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-left py-2 pr-3">Submitted</th>
                  <th className="text-left py-2 pr-3">Target publish</th>
                  <th className="text-left py-2 pr-3">Est. publish</th>
                  <th className="text-left py-2">Last change</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((e) => (
                  <tr key={e.id} className="border-b border-[#dcd3c6]/40" data-testid={`queue-row-${e.id}`}>
                    <td className="py-2 pr-3">
                      {e.priority && <Star size={14} className="text-[#876a4f] fill-[#c19872]" data-testid={`queue-priority-${e.id}`} />}
                    </td>
                    <td className="py-2 pr-3">
                      <Link to={`/admin/events/${e.id}`} className="font-medium hover:text-[#876a4f]">{e.event_name}</Link>
                    </td>
                    <td className="py-2 pr-3"><PubBadge pub={e.publication} /></td>
                    <td className="py-2 pr-3 text-xs">{e.status}</td>
                    <td className="py-2 pr-3 text-xs">{fmt(e.submitted_at)}</td>
                    <td className="py-2 pr-3 text-xs" data-testid={`target-${e.id}`}>
                      {e.target_publish_date || "—"}
                      {e.overdue && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[#b35c5c]" data-testid={`overdue-${e.id}`}>
                          <AlertTriangle size={12} /> overdue
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs" data-testid={`estimate-${e.id}`}>{e.estimated_publish_date || "—"}</td>
                    <td className="py-2 text-xs text-[#5c5852]">
                      {e.status_history.length > 0
                        ? `${e.status_history[e.status_history.length - 1].status} · ${fmt(e.status_history[e.status_history.length - 1].at)}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

const Kpi = ({ label, value, testid }) => (
  <div className="bg-white border border-[#dcd3c6] p-4" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-[#5c5852] mb-1">{label}</p>
    <p className="text-2xl font-bold">{value}</p>
  </div>
);

const Panel = ({ title, children, testid }) => (
  <section className="bg-white border border-[#dcd3c6] p-5" data-testid={testid}>
    <h2 className="btw-overline mb-4">{title}</h2>
    <div className="divide-y divide-[#dcd3c6]/60">{children}</div>
  </section>
);

const Empty = ({ children }) => (
  <div className="text-sm text-[#5c5852] italic py-2">{children}</div>
);

const CountRows = ({ obj, upper }) => (
  <>
    {Object.keys(obj || {}).length === 0 && <Empty>No submissions in range.</Empty>}
    {Object.entries(obj || {}).map(([k, v]) => (
      <div key={k} className="flex items-center justify-between py-2 text-sm" data-testid={`count-${k}`}>
        <span className={upper ? "uppercase" : ""}>{k}</span>
        <span className="font-bold">{v}</span>
      </div>
    ))}
  </>
);

const IncompleteRow = ({ e }) => (
  <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-testid={`incomplete-${e.id}`}>
    <Link to={`/admin/events/${e.id}`} className="font-medium hover:text-[#876a4f] text-sm">{e.event_name}</Link>
    <span className="text-xs text-[#5c5852]">{e.days_since_activity}d since activity</span>
  </div>
);
